import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  useCancelAdminBillingInvoice,
  useIssueAdminBillingInvoice,
  type AdminBillingInvoiceDetail,
} from '../../hooks/use-admin-billing'
import { apiErrorMessage } from './billing-errors'

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** BillingInvoiceActions issues a draft with the accounting tool's number, or cancels an invoice. */
export function BillingInvoiceActions({ invoice, part }: { invoice: AdminBillingInvoiceDetail; part: 'issue' | 'cancel' }) {
  const { t } = useTranslation('admin')
  const issue = useIssueAdminBillingInvoice()
  const cancel = useCancelAdminBillingInvoice()
  const [number, setNumber] = useState('')
  const [issuedOn, setIssuedOn] = useState(today())
  const [reason, setReason] = useState('')
  const [creditNote, setCreditNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (invoice.status === 'cancelled') return null
  const draft = invoice.status === 'draft'
  if (part === 'issue' && !draft) return null

  const submitIssue = () => {
    if (!number.trim()) {
      setError(t('billing.invoices.issue.invalid'))
      return
    }
    setError(null)
    issue.mutate(
      { params: { path: { uid: invoice.uid } }, body: { number: number.trim(), issued_on: issuedOn || undefined } },
      { onError: (e) => setError(apiErrorMessage(e, t('billing.invoices.issue.error'))) },
    )
  }
  const submitCancel = () => {
    if (!draft && !creditNote.trim()) {
      setError(t('billing.invoices.cancel.credit_note_required'))
      return
    }
    setError(null)
    cancel.mutate(
      {
        params: { path: { uid: invoice.uid } },
        body: { reason: reason.trim() || undefined, credit_note_number: creditNote.trim() || undefined },
      },
      { onError: (e) => setError(apiErrorMessage(e, t('billing.invoices.cancel.error'))) },
    )
  }

  const errorBox = error && (
    <div className="text-sm text-destructive" data-testid="billing-invoice-error">
      {error}
    </div>
  )
  if (part === 'issue') {
    return (
    <section className="space-y-4" data-testid="billing-invoice-issue">
      <div className="bg-card rounded-lg border border-border p-4 space-y-3" data-testid="billing-issue-form">
          <h2 className="text-lg font-semibold">{t('billing.invoices.issue.title')}</h2>
          <ol className="list-decimal pl-5 text-sm space-y-1" data-testid="billing-issue-checklist">
            <li>{t('billing.invoices.issue.step_copy')}</li>
            <li>{t('billing.invoices.issue.step_issue')}</li>
            <li>{t('billing.invoices.issue.step_paste')}</li>
          </ol>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label>{t('billing.invoices.issue.number')}</Label>
              <Input value={number} onChange={(e) => setNumber(e.target.value)} className="w-56" data-testid="billing-issue-number" />
            </div>
            <div className="space-y-1">
              <Label>{t('billing.invoices.issued_on')}</Label>
              <Input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} className="w-44" data-testid="billing-issue-date" />
            </div>
            <Button onClick={submitIssue} disabled={issue.isPending} data-testid="billing-issue-submit">
              {t('billing.invoices.issue.submit')}
            </Button>
          </div>
        </div>
      {errorBox}
    </section>
    )
  }
  return (
    <section className="space-y-4" data-testid="billing-invoice-actions">
      <h2 className="text-lg font-semibold">{t('billing.invoices.actions_title')}</h2>
      <div className="bg-card rounded-lg border border-border p-4 space-y-3" data-testid="billing-cancel-form">
        <p className="text-sm text-muted-foreground">{draft ? t('billing.invoices.cancel.help_draft') : t('billing.invoices.cancel.help_issued')}</p>
        <div className="flex flex-wrap items-end gap-3">
          {!draft && (
            <div className="space-y-1">
              <Label>{t('billing.invoices.cancel.credit_note')}</Label>
              <Input value={creditNote} onChange={(e) => setCreditNote(e.target.value)} className="w-56" data-testid="billing-cancel-credit-note" />
            </div>
          )}
          <div className="space-y-1 grow">
            <Label>{t('billing.invoices.cancel.reason')}</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="billing-cancel-reason" />
          </div>
          <Button variant="outline" onClick={submitCancel} disabled={cancel.isPending} data-testid="billing-cancel-submit">
            {t('billing.invoices.cancel.submit')}
          </Button>
        </div>
      </div>
      {errorBox}
    </section>
  )
}
